import { POS_BASE } from "@/lib/api/client";
import { LIVE } from "@/lib/data/source";
import { normaliseLocale, type CounterLocale } from "@/lib/i18n/config";

/**
 * The signed-in person's language on their staff profile — `preferredLocale` on the sign-in
 * identity, owned by user-management and reached through pos-orchestrator's `/pos/me/preferences`
 * (ADR-0015 D1: the till talks only to its edge). The route acts on the session's own subject; there
 * is no staff id in the path to get wrong.
 *
 * Mock mode (no edge) has no profile: reads answer "no choice" and writes succeed locally, so the
 * device cache in `staff-locale.ts` carries the choice.
 */
type Preferences = { preferredLocale: string | null };

/** `undefined` = the edge could not be reached (keep the cache); `null` = no choice on the profile. */
export async function fetchStaffPreference(): Promise<CounterLocale | null | undefined> {
  if (!LIVE) return undefined;
  try {
    const res = await fetch(`${POS_BASE}/me/preferences`, { credentials: "include" });
    if (!res.ok) return undefined;
    const body = (await res.json()) as Preferences;
    return normaliseLocale(body.preferredLocale) ?? null;
  } catch {
    return undefined;
  }
}

/** True when the profile holds the choice; false when only this device does. */
export async function saveStaffPreference(locale: CounterLocale | null): Promise<boolean> {
  if (!LIVE) return false;
  try {
    const res = await fetch(`${POS_BASE}/me/preferences`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ preferredLocale: locale } satisfies Preferences),
    });
    return res.ok;
  } catch {
    return false;
  }
}
